import Content from '../../../shared/types/enums/content';
import FastEvent from '../../../shared/classes/fastEvent';
import MediaProduction from '../../../shared/classes/mediaProduction';
import Rail from '@shared/classes/rail';
import goToPlayer from '../../actions/player/goToPlayer';
import goToSeasonExtended from '../../../shared/actions/season/goToSeasonExtended';
import nextEpisodeRequest from '../../../shared/api/nextEpisode/nextEpisodeRequest';
import { IMediaState } from '../../types/interfaces/states';
import { IPlayerState } from '../../../shared/types/interfaces/states';
import { IRouteState } from '../../../shared/types/interfaces/states';
import { PlayerState } from '../../../shared/types/enums/player';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { popRoute } from './route.thunk';
import { resetPlayer, setNextEpisode } from '../slices/player.slice';
import { setPosition, setState } from '../slices/media.slice';

const NEXT_EPISODE_MARK = 30;

// Thunks

const fetchNextEpisode = createAsyncThunk(
  'fetch/nextEpisode',
  (asset: FastEvent | MediaProduction, thunk) => {
    try {
      if (!asset || asset.catchup) return;
      if (!asset.seasonEpisodes && !asset.episodeNumber && !asset.seasonNumber)
        return;

      const {
        router: { params },
      } = thunk.getState() as { router: IRouteState };

      // If episodes available, fetch next episode
      const episodes =
        (params?.stack[params?.stack.length - 1]?.rails as Rail[])
          ?.map((item) => item.data)
          .flat() ||
        params?.stack[params?.stack.length - 1]?.items ||
        [];
      const nextEpisode =
        (episodes as (FastEvent | MediaProduction)[])?.find(
          (item: FastEvent | MediaProduction) => {
            return (
              ((item?.episodeNumber || 1) === (asset?.episodeNumber || 1) + 1 &&
                (item?.seasonNumber || 0) === (asset.seasonNumber || 0)) ||
              (asset?.episodeNumber === asset?.seasonEpisodes &&
                item?.episodeNumber === 1 &&
                item?.seasonNumber === (asset.seasonNumber || 0) + 1)
            );
          }
        ) || undefined;

      // Load next asset from season data if possible
      if (nextEpisode) {
        thunk.dispatch(setNextEpisode({ asset: nextEpisode }));
      }
      // Else, request next episode from BE
      else if ([Content.FAST_EVENT, Content.SHOW].includes(asset?.type)) {
        nextEpisodeRequest(asset?.eventId).then((nextEpisode) => {
          // Load next episode if no season data
          // or if it is next season's first episode
          if (
            !episodes?.length ||
            (nextEpisode?.seasonNumber === (asset?.seasonNumber || 1) + 1 &&
              nextEpisode?.episodeNumber === 1)
          ) {
            nextEpisode &&
              thunk.dispatch(setNextEpisode({ asset: nextEpisode }));
          }
        });
      }
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

const loadSeasonExtended = createAsyncThunk(
  'load/seasonExtended',
  (id: string | undefined, thunk) => {
    try {
      const {
        player: {
          nextEpisode: { asset },
        },
        router: { params },
      } = thunk.getState() as { player: IPlayerState; router: IRouteState };

      if (!asset) return;

      // If coming from season extended, fetch season data

      const seasonExtended =
        params.stack.find((item) => {
          return item?.isSeason;
        }) || undefined;

      const currentAsset = params?.current?.content as
        | FastEvent
        | MediaProduction;

      const currentSeason = currentAsset?.seasonNumber === asset?.seasonNumber;

      // If season extended was loaded previously and is the current season, pop route
      if (currentSeason && seasonExtended) {
        thunk.dispatch(popRoute());
      }
      // If content is a FastEvent, load via seasonId
      else if ([Content.FAST_EVENT, Content.SHOW].includes(asset?.type)) {
        goToSeasonExtended({ seasonId: asset?.seasonId });
      }
      // If content is a MediaProduction, load via id
      else if (asset?.type === Content.MEDIA_PRODUCTION) {
        const { id, isRecord, seasonNumber } = (asset as MediaProduction) || {};
        goToSeasonExtended({
          id,
          isRecord,
          seasonNumber,
        });
      }
      thunk.dispatch(resetPlayer());
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

const playerEndAction = createAsyncThunk('action/playerEnd', (data, thunk) => {
  try {
    const {
      player: {
        nextEpisode: { asset },
      },
    } = thunk.getState() as { player: IPlayerState };

    // Clear player data
    thunk.dispatch(setPosition({ position: 0, seek: false }));
    thunk.dispatch(setState(PlayerState.ENDED));
    thunk.dispatch(resetPlayer());

    setTimeout(() => {
      // Load new player if next episode is enabled
      if (asset) {
        goToPlayer({ asset, forceRestart: true, nextEpisode: true });
      }
      // Else, go to previous page
      else {
        thunk.dispatch(popRoute());
      }
    });
  } catch (error) {
    thunk.rejectWithValue(error);
  }
});

const updateNextEpisode = createAsyncThunk(
  'update/nextEpisode',
  (data, thunk) => {
    try {
      const {
        media: { duration, live, position, state },
        player: {
          nextEpisode: { asset },
        },
      } = thunk.getState() as { media: IMediaState; player: IPlayerState };

      if (live || !asset || state === PlayerState.SEEKING) return;

      const remainder = duration - position;
      const enabled =
        [
          PlayerState.PAUSE,
          PlayerState.PLAY,
          PlayerState.PLAYING,
          PlayerState.SEEK,
          PlayerState.SEEKING,
        ].includes(state) && remainder <= NEXT_EPISODE_MARK;
      const percentage = enabled
        ? (1 - remainder / NEXT_EPISODE_MARK) * 100
        : 0;

      thunk.dispatch(
        setNextEpisode({
          enabled,
          percentage,
        })
      );
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

export {
  fetchNextEpisode,
  loadSeasonExtended,
  playerEndAction,
  updateNextEpisode,
};
